import React, { useEffect, useState, ReactNode } from "react";
import "tailwindcss/tailwind.css";
import "./styles/global.css";
import "./styles/antd.fixes.scss";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  RouteProps,
} from "react-router-dom";
import "./config/Amplify";
import { StoreProvider } from "./store/Store";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import {
  silentAuth,
  fetchILivestockUser,
  removeDeferredLoadingFlags,
} from "./store/actions";
import { useStore } from "./store/useStore";
import { AbstractRouteType } from "./util/route-mapper";
import { routes } from "./util/route-mapper/routes";
import { LoadingPage } from "./pages/LoadingPage";
import { defaultIlivestockQuery, DeferredLoadingFlags } from "./util";
import { useCookies } from "react-cookie";
import { v4 as uuid } from "uuid";
import AppMigrationRequiredRoute from "./components/AppMigrationRequiredRoute/AppMigrationRequiredRoute";
import { environment } from "./config/Environment";
import CustomerProvider from "./util/providers/CustomerProvider";
import { CookiesProvider } from "react-cookie";
import IntercomLiveChat from './components/IntercomLiveChat'
import { destroySession } from "./store/actions";
import { useHistory } from "react-router-dom";

function App() {
  return (
    <StoreProvider>
      <CookiesProvider>
        <Router>
          <CustomerProvider>
            <IntercomLiveChat appId={environment.intercomAppId} />
            <TransitioningApp />
          </CustomerProvider>
        </Router>
      </CookiesProvider>
    </StoreProvider>
  );
}

export const TransitioningApp = () => {
  const { state, dispatch } = useStore();
  const [cookies, setCookie] = useCookies(["device-uuid"]);
  const history = useHistory();

  if (!cookies["device-uuid"])
    setCookie("device-uuid", uuid().toUpperCase(), {
      domain: environment.host.domain,
      expires: new Date(2030, 1, 1),
    });

  /**
   * Always try to silently authenticate whenever the app loads
   */
  useEffect(() => {
    const init = async () => {
      try {
        await dispatch(silentAuth());
      } catch (error) {
        console.error(error);
      }
      dispatch(removeDeferredLoadingFlags(DeferredLoadingFlags.SILENT_AUTH));
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Whenever the user in the state changes, we want to reload all that users resources.
   * A user changes after a successful login, or a logout and then relogin.
   */
  useEffect(() => {
    if (!state.user || !state.user.signInUserSession) return;

    // 13 Mar 24 PC:
    // Clear out any existing sessions so users are redirected to the login
    // page which now tells them the site is offline
    const logout = async () => {
      try {
        await dispatch(destroySession());
        history.push("/login");
      } catch (error) { }
    }

    logout()

    //dispatch(fetchILivestockUser(defaultIlivestockQuery));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user]);

  /**
   * If silent authentication is trying to happen, ensure we block the page until done
   */
  if (state.loading.has(DeferredLoadingFlags.SILENT_AUTH))
    return <LoadingPage tip="Authenticating" />;

  /**
   * Maps routes from a route mapping function and
   * recursively generates routes based on child route mappings
   * @param routes
   */
  const mapRoutes = (
    routes: AbstractRouteType[]
  ): React.ReactElement<RouteProps>[] => {
    // @ts-ignore
    return routes
      .map((route, idx) => {
        const { _protected, _appMigrationRequired, childRoutes } = route;
        let children: ReactNode[] = childRoutes?.length
          ? mapRoutes(childRoutes)
          : [];

        // If the user is created via legacy migration, and the route is
        // marked as blocked for legacy users that haven't completed setup
        // via the mobile application.
        if (_appMigrationRequired) {
          return [
            <AppMigrationRequiredRoute key={idx} {...route} />,
            ...children,
          ];
        }

        // We don't even render the route if the user does not have roles to view it
        // TODO make unique routes and instead display a 401 page
        // if (!hasRoles(requiredRoles).all()) return null;
        if (_protected) {
          return [<ProtectedRoute key={idx} {...route} />, ...children];
        }

        return [<Route key={idx} {...route} />, ...children];
      })
      .flat(5) // resolve to 5 deep, so 5 children max, adjust if required
      .filter((r) => !!r); // remove the nulls
  };

  const mappedRoutes = mapRoutes(routes);
  return <Switch>{mappedRoutes}</Switch>;
};

export default App;
